<template lang="pug">
.page.product(ref="page")
  error-layout(v-if="$fetchState.error" :error="$fetchState.error")

  loading(v-else-if="!record" text="Loading product..." min-height="300px")

  .container(v-else)
    el-breadcrumb.hidden-xs-only(separator="/")
      el-breadcrumb-item(
        v-for="i, k in breadcrumbs"
        tag="a"
        :key="k"
        :to="i.to"
        :title="i.name")
          span(v-text="i.name")

    template(v-if="record")
      .layout-grid
        .area-images
          product-images-swiper(
            :product="{ ...record, variants: sortedVariants }"
            :variant.sync="selected")
          section.sharing
            span.pr-1 Share this product
            sharer(caption="Share this product")

        .area-heading
          .general-information
            .name
              h1.pf-break-word(v-text="record.name")
              client-only
                button.favorite(
                  v-if="showFavoriteBtn"
                  @click.stop="favorite"
                  v-tooltip="selected.favorite_products_id ? 'Remove from favorites' : 'Add to favorites'"
                  :disabled="saving")
                  .heart(:class="{animated: selected.favorite_products_id}")
            nuxt-link.brand.flex.pf-nowrap(
              v-if="record.brand"
              :to="$links.brand({ id: record.brand.id, name: record.brand.name })" v-click="() => $amplitude.trackBrand({ zone: 'PRODUCT_DETAIL_BRAND', product_id: $route.params.id })")
              .logo
                img(v-image="record.brand.logo_url.medium_url")
              .name.pf-ellipsis(v-text="record.brand.name")
              span.address.pf-ellipsis.hidden-xs-only(v-if="$options.filters.brandAddress(record.brand)") {{ record.brand | brandAddress }}
            .shipping(v-if="selected")
              ExpressTag(v-if="selected.is_warehoused_variant" explained)
              GrowTag(v-else-if="selected.is_pod_grow_variant" explained)
              DirectTag(v-else-if="isWarehouseBuyer && selected.region_type === REGION_TYPES.PFD" explained)

        .area-body
          section.tags(v-if="tags.length")
            .tags
              product-tag-stamp(
                v-for="i, k in tags"
                :key="k"
                :data="i"
                :linked="true")

          section.order-limit(v-if="orderLimit")
            order-limit-alert(v-bind="orderLimit")

          // For Head Buyer only
          section.price(v-if="isHeadBuyer")
            ProductPriceStamp(
              :product="record"
              :variant="selected"
              :quantity="quantity")

          // New design ui/ux for multiple sku add-to-cart
          section.variants
            MultipleSkusAddToCart.plain(
              ref="skusAddToCart"
              :variants="sortedVariants"
              :product="record"
              :selectedId.sync="selectedId"
              showImage
              showVariantName
              showVariantId
              showBadge
              showPrice
              showPriceChange
              showQuantityChange
              showAddtoCartBtn
              :showRequestSample="sampleRequestsEnabled")

          // For LP and Vendor (not vendor product owner, not buyer)
          section.regions(v-if="showAvailableRegions")
            .header
              span.caption Available in
            .tags
              el-tag(
                type="region"
                size="mini"
                v-for="i, k in selected.regions_information"
                :key="k") {{ i.name }}

          // For Pre-order or Guest request purchase
          section.pre-order.hidden-xs-only(v-if="isComingSoon")
            .form
              el-button.pre-order(type="primary" @click="preOrder")
                span Pre-order
          section.requesting(v-else-if="isPurchaseRequestSupported")
            .add-to-purchase-request
              el-button.btn(type="primary" @click="requestToPurchase")
                i.bx.bxs-lock-open.bx-5
                span.ml-1 Wholesale Pricing

        .area-specifications
          section
            dl.metas(v-if="selected")
              template(v-if="selected.barcode_code")
                dt Unit UPC / EAN
                dd.upc
                  client-only
                    el-popover(placement="top" width="300" trigger="click")
                      barcode.barcode(
                        :value="selected.barcode_code"
                        :format="selected.barcode_type"
                        tag="svg"
                      )
                      .upc-references(slot="reference")
                        .upc-number.ml-1 {{ selected.barcode_code }}
                        img(src="/img/barcode.svg")
              template(v-if="isBuyerAndActiveSku")
                dt Availability
                dd.pd-availability
                  span(:class="selected.availability && selected.availability.replace('_', '-')") {{ selected.availability | productVariantAvailability }}
                  a.similar-product-linked(v-if="!isVendor && !isLP && isOutofStock" href="#related-products")
                    | Check for similar products
                    i.bx.bx-down-arrow-alt.bx-4.icon

              template(v-if="headBuyerHasAssignedRegions")
                dt Availability
                dd.pd-availability
                  template(v-if="selected.variants_regions_info")
                    .region-status(v-for="(region, index) in selected.variants_regions_info.region_info" )
                      span &nbsp;{{ region.region_id | region('abbreviated_name') }} -
                      span(:class="PRODUCT_AVAILABILITIES[region.availability].CLASS")  {{ PRODUCT_AVAILABILITIES[region.availability].TEXT }}
                      span(v-if="selected.variants_regions_info.region_info.length > 1 && index < selected.variants_regions_info.region_info.length - 1") ,
                  .not-available(v-else) Not available
              template(v-if="isBuyer && selected.availability === PRODUCT_AVAILABILITY.COMING_SOON && selected.inventory_receiving_date")
                dt New Inventory Arriving
                dd {{ selected.inventory_receiving_date | date }}

              template(v-if="isBuyerAndActiveSku")
                template(v-if="!isPodExpressSku && !isPodGrowSku")
                  template(v-if="isMoqSku && selected._moq > 1")
                    dt Minimum Order Quantity
                    dd.limit--moq {{ selected._moq | int }} cases
                  template(v-else-if="isMovSku")
                    dt Minimum Order Value
                    dd.limit--mov {{ selected.mov | cents(true, false) }}
                  template(v-else)
                    dt Minimum Purchase
                    dd.limit--none Not required

              template(v-if="isBuyer || isHeadBuyer || isProductOwner")
                template(v-if="isHeadBuyer")
                  template(v-if="selected.variants_regions_info && selected.variants_regions_info.variants_regions_margin_range")
                    dt Gross-margin
                    dd {{ selected.variants_regions_info.variants_regions_margin_range }}
                template(v-else)
                  template(v-if="selected.margin_str")
                    dt Gross-margin
                    dd {{ selected.margin_str }}
                template(v-if="isHeadBuyer && variantRegionsInfoSelected && variantRegionsInfoSelected.variants_regions_max_msrp")
                  dt MSRP
                  dd  {{ variantRegionsInfoSelected.variants_regions_max_msrp | cents }}
                template(v-if="selected.msrp_str")
                  dt MSRP
                  dd  {{ selected.msrp_str }}
                template(v-if="record.size")
                  dt Unit Dimensions
                  dd  {{ record.size }}
                template(v-if="record.case_size")
                  dt Case Dimensions
                  dd  {{ record.case_size }}
                template(v-if="record.volume_unit")
                  dt Unit Size
                  dd  {{ record.volume }}&nbsp;{{ record.volume_unit }}
              template(v-if="selected.case_units")
                dt Case Pack
                dd
                  | {{ selected.case_units }}&nbsp;
                  span {{selected.case_units > 1 ? 'units' : 'unit' }}&nbsp;
                  | per case
              template(v-if="isBuyer && selected.region_type === REGION_TYPES.PFD && selected.avg_leadtime")
                dt Avg. Lead Time
                dd {{ selected.avg_leadtime | day }}

        .area-buyer-disclamer(v-if="isBuyerDisclaimer")
          ul.disclaimer.mb-0
            li
              strong Disclaimer:
              ul
                li
                  span
                    | Buyer acknowledges that the retail sale of Cannabidiol (CBD) in food and beverage products to the public for general consumption may not be legal in some jurisdictions.
                li
                  span
                    | Buyer assumes all responsibility to any third parties it chooses to sell CBD products to, and holds Pod Foods Co and its employees harmless against any legal actions that arise as a result of the sale.

      el-card.product-summary-card(v-if="isProductOwner && selected && selected.variants_regions && selected.variants_regions.length" shadow="never" :title="null")
        product-owner-summary(
          :variant="selected"
          :product="record"
        )

      el-row(:gutter="20")
        el-col(:md="16")
          el-card.product-card(shadow="never" :title="null")
            el-tabs.product-tabs(v-if="selected" v-model="tabSelected")
              el-tab-pane(label="Details" name="detail" active)
                dl.metas
                  template(v-if="selected")
                    dt Manufactured in
                    dd(v-text="selected.manufactured_in_str")
                  template(v-if="selected.storage_shelf_life_obj")
                    dt Storage shelf life
                    dd {{ selected.storage_shelf_life_obj.days | day }} {{ selected.storage_shelf_life_obj.condition }}
                  template(v-if="selected.retail_shelf_life_obj")
                    dt Retail shelf life
                    dd {{ selected.retail_shelf_life_obj.days | day }} {{ selected.retail_shelf_life_obj.condition }}
                  template(v-if="selected.min_temperature")
                    dt Temperature requirements
                    dd {{ selected.min_temperature, selected.max_temperature | temperatureRange }}

                section.ingredients
                  .label Ingredients
                  .content {{ selected.ingredients.join(', ') }}
                section.product-qualities
                  .label Product Qualities
                  ul
                    li(
                      v-for="i, k in selected.qualities"
                      v-text="i"
                      :key="k")

              el-tab-pane(label="Product Description" name="description")
                section.description(v-html="selected.description")
        el-col(:md="8")
          el-card.product-card(shadow="never" :title="null")
            .section-title Nutrition Labels
            product-nutrition-images-swiper(v-if="selected" :nutritions="selected.nutrition_label_urls")

      section.related(id="related-products" ref="relatedProducts" v-if="isBuyer || isHeadBuyer || !isLogged")
        el-card.product-card.recommend-buyer-shopping(shadow="never" :title="null")
          .header.flex
            span.caption Buyers are also shopping for
            //- nuxt-link.action.linked(:to="{name: 'products', query: {pid: record.id}}") Explore more →
          product-recommendations(:product="record" :selected="selected")

      // Old UI/UX: Add to cart & Request Sample for mobile devices (ProductDrawer)
      //- ProductDrawer(:product="record" :variant.sync="selected")
      debug(:data="record")

  call-for-action-section
</template>

<script>
import { mapGetters } from 'vuex'
import { findKey, orderBy, uniqBy, sum } from 'lodash'
import { WEBSITE_BASE_URL, PRODUCT_AVAILABILITY, REGION_TYPES, LIMIT_TYPES, ERROR_HANDLING_MANUAL } from '~/settings'

import ErrorLayout from '~/layouts/error'
import Barcode from '~/components/Barcode'
import CallForActionSection from '~/components/home/CallForActionSection'
import ProductImagesSwiper from '~/components/ProductImagesSwiper'
import ProductNutritionImagesSwiper from '~/components/products/ProductNutritionImagesSwiper'
import MultipleSkusAddToCart from '~/components/carts/MultipleSkusAddToCart'
import ProductTagStamp from '~/components/stamps/ProductTagStamp'
import ProductPriceStamp from '~/components/stamps/ProductPriceStamp'
import ProductRecommendations from '~/components/ProductRecommendations'
import ProductOwnerSummary from '~/components/products/ProductOwnerSummary'
import Sharer from '~/components/Sharer'
import ExpressTag from '~/components/tags/ExpressTag'
import DirectTag from '~/components/tags/DirectTag'
import GrowTag from '~/components/tags/GrowTag'
import OrderLimitAlert from '~/components/products/OrderLimitAlert'

const PRODUCT_AVAILABILITIES = {
  [PRODUCT_AVAILABILITY.IN_STOCK]: {
    TEXT: 'In Stock',
    CLASS: 'in-stock'
  },
  [PRODUCT_AVAILABILITY.SOLD_OUT]: {
    TEXT: 'Out of stock',
    CLASS: 'sold-out'
  },
  [PRODUCT_AVAILABILITY.COMING_SOON]: {
    TEXT: 'Launching soon',
    CLASS: 'coming-soon'
  }
}

export default {
  name: 'CatalogDetail',

  components: {
    ErrorLayout,
    Barcode,
    CallForActionSection,
    ProductImagesSwiper,
    ProductNutritionImagesSwiper,
    ProductOwnerSummary,
    ProductTagStamp,
    ProductPriceStamp,
    MultipleSkusAddToCart,
    ProductRecommendations,
    Sharer,
    ExpressTag,
    DirectTag,
    GrowTag,
    OrderLimitAlert
  },

  beforeRouteLeave (to, from, next) {
    if (!this.$refs.skusAddToCart) return next()

    const skusAddToCart = this.$refs.skusAddToCart
    const quantities = Object.values(skusAddToCart.quantities)

    if (skusAddToCart.onlyOneActiveSku && sum(quantities) <= 1) return next()
    if (!skusAddToCart.submittable || !sum(quantities)) return next()

    this.$confirm('You forgot to add an item to the cart. Would you like to add it?',
      {
        confirmButtonText: 'Yes, add to cart',
        cancelButtonText: 'No, I wouldn\'t',
        type: 'warning'
      })
    .then(() => {
      skusAddToCart.addToCart()
      return false
    })
    .catch(() => {
      return next()
    })
  },

  layout: 'catalog',

  data () {
    return {
      PRODUCT_AVAILABILITIES,
      PRODUCT_AVAILABILITY,
      record: null,
      selectedId: null,
      tabSelected: 'detail',
      quantity: 1,
      tab: 'details',
      saving: false,
      adding: false,
      tracked: false
    }
  },

  fetch () {
    return this
      .fetch()
      .catch(e => {
        const error = { statusCode: e.response?.status || 404 }
        if (process.server) {
          this.$nuxt.context.res.statusCode = error.statusCode
          this.$fetchState.error = error
        } else {
          this.$nuxt.error(error)
        }
      })
  },

  head () {
    return {
      ...this.$seo.head(this.seo),
      link: [{
        hid: 'canonical',
        rel: 'canonical',
        href: `${WEBSITE_BASE_URL}${this.$route.path}`
      }]
    }
  },

  computed: {
    ...mapGetters({
      apiRole: 'authentication/apiRole',
      isBuyer: 'authentication/isBuyer',
      isHeadBuyer: 'authentication/isHeadBuyer',
      headBuyerHasAssignedRegions: 'authentication/headBuyerHasAssignedRegions',
      isLogged: 'authentication/logged',
      isWarehouseBuyer: 'authentication/isWarehouseBuyer',
      isLP: 'authentication/isLP',
      isVendor: 'authentication/isVendor',
      vendorCompanyId: 'authentication/vendorCompanyId',
      currentUser: 'authentication/user',
      isPreApprovalBuyer: 'authentication/isPreApprovalBuyer',
      sampleRequestsEnabled: 'authentication/sampleRequestsEnabled',
      productTags: 'master/productTags'
    }),

    REGION_TYPES: () => REGION_TYPES,

    isBuyerAndActiveSku () {
      return this.isBuyer && !this.selected.__INACTIVE
    },

    showAvailableRegions () {
      if (this.isProductOwner || !this.selected.__INACTIVE || this.isHeadBuyer)
        return false

      return this.selected?.regions_information?.length
    },

    showFavoriteBtn () {
      return (this.isBuyer || this.isHeadBuyer) && !this.selected.__INACTIVE
    },

    isPodExpressSku () {
      return this.selected?.is_warehoused_variant
    },

    isPodGrowSku () {
      return this.selected?.is_pod_grow_variant
    },

    id () {
      return this.$route.params.id
    },

    selected: {
      get () {
        if (!this.record) return null
        return this.record.variants[this.selectedId] || null
      },

      set ({ id = null } = {}) {
        this.selectedId = id
      }
    },

    orderLimit () {
      if (!this.isBuyer || this.selected.__INACTIVE) return null
      if (this.isPodExpressSku || this.isPodGrowSku) return null
      else if (this.isMoqSku && this.selected._moq > 1)
        return {
          type: LIMIT_TYPES.MOQ,
          value: this.selected._moq
        }
      else if (this.isMovSku)
        return {
          type: LIMIT_TYPES.MOV,
          value: this.selected.mov
        }
      else
        return {
          type: LIMIT_TYPES.NONE
        }
    },

    variantRegionsInfoSelected () {
      return this.selected?.variants_regions_info || {}
    },

    category () {
      if (!this.record) return null
      return this.$store.getters['master/productCategoryById'](this.record.product_category_id)
    },

    sortedVariants () {
      const { master_variant_id, variants } = this.record
      const sorted = master_variant_id
        ? orderBy(variants, v => v.id !== master_variant_id)
        : variants

      return orderBy(sorted, ['__INACTIVE', 'cartable'], ['desc', 'desc'])
    },

    tags () {
      const selectedTags = this.selected?.tag_ids?.length
        ? this.productTags.filter(i => this.selected.tag_ids.includes(i.id))
        : []
      const catalogTags = this.record?.catalog_tags || []

      return uniqBy([...selectedTags, ...catalogTags], 'id')
    },

    type () {
      if (!this.record) return null
      return this.$store.getters['master/productTypeById'](this.record.product_type_id)
    },

    breadcrumbs () {
      const res = [{
        name: 'Catalog',
        to: {
          name: 'products'
        }
      }]
      if (this.category) {
        res.push({
          name: this.category.name,
          to: {
            name: 'products',
            query: {
              category_id: this.category.id
            }
          }
        })
        if (this.type) res.push({
          name: this.type.name,
          to: {
            name: 'products',
            query: {
              category_id: this.category.id,
              'q[product_type_id]': this.type.id
            }
          }
        })
      }
      if (this.record) res.push({
        name: this.record.name,
        to: null
      })
      return res
    },

    isProductOwner () {
      if (!this.isVendor || !this.record) return false
      return this.vendorCompanyId === this.record.vendor_company_id
    },

    isComingSoon () {
      return this.isBuyer && this.selected && this.selected.availability === PRODUCT_AVAILABILITY.COMING_SOON
    },

    isOutofStock () {
      return this.isBuyer && this.selected && this.selected.availability === PRODUCT_AVAILABILITY.SOLD_OUT
    },

    isPurchaseRequestSupported () {
      return !this.isLogged
    },

    isMovSku () {
      return this.selected &&
        this.selected.limit_type === LIMIT_TYPES.MOV &&
        this.selected.mov
    },

    isMoqSku () {
      return this.selected &&
        this.selected.limit_type === LIMIT_TYPES.MOQ &&
        this.selected._moq
    },

    isBuyerDisclaimer () {
      if (!this.isBuyer) return false
      const nyBuyer = this.currentUser?.store?.region_name?.includes('New York')
      const cannabidiolProduct = this.category ? ['CBD', 'Hemp'].includes(this.category.name) : false
      const cannabidiolSku = Object.values(this.record.variants)
        .filter(e => !e.__INACTIVE)
        .some((variant) => {
          return variant.qualities && (variant.qualities.includes('CBD') || variant.qualities.includes('Hemp'))
        })

      return nyBuyer && (cannabidiolProduct || cannabidiolSku)
    },

    seo () {
      if (!this.record) return {}
      return {
        title: this.record.name,
        description: this.selected?.description,
        image: this.selected ? this.selected.image_urls.product : null,
        type: 'og:product',
        properties: {
          // 'product:price:amount': null,
          'product:price:currency': 'USD'
        },
        path: this.$route.path,
        sku: this.selectedId,
        brand: {
          id: this.record.brand.id,
          name: this.record.brand.name
        }
      }
    }
  },

  watch: {
    selectedId () {
      const res = /^(\d+)(?:-)?/ig.exec(this.$route.query.sku)
      const skuId = res ? +res[1] : null
      if (!this.selected || skuId === this.selected.id) return

      this.$router
        .replace(this.$links.product({
          id: this.record.id,
          name: this.record.name,
          brand: this.record.brand.name,
          ...(this.selected
              ? {
                variant: this.selected.name,
                variantId: this.selected.id
              }
              : {}
          ),
          query: {
            i: this.$route.query?.i
          },
          hash: this.$route.hash
        }))
        .catch(this.$debug.log)
    },
    '$route.hash': {
      immediate: true,
      deep: true,
      handler (val) {
        if (this.$refs && this.$refs.relatedProducts && val === '#related-products') {
          this.$refs.relatedProducts.scrollIntoView({ behavior: 'smooth' })
        }
      }
    }
  },

  mounted () {
    if (this.$refs && this.$refs.relatedProducts && this.$route.hash === '#related-products') {
      this.$refs.relatedProducts.scrollIntoView({ behavior: 'smooth' })
    }
  },

  methods: {
    handleTrack (event) {
      const widget = this.$preferences.get('vendorBrandWidget')
      const brand = this.$preferences.get('vendorBrand')
      this.$amplitude.track(
        event,
        {
          product_id: this.id,
          sku_id: this.selectedId,
          ...(widget
            ? {
              zone: `widget-${widget}`,
              brand_name: brand
            }
            : {}
          )
        })
    },

    fetch () {
      if (!this.id) return
      return this.$api.catalog
        .getProductDetails(this.id, { [ERROR_HANDLING_MANUAL]: true })
        .then(record => {
          let id = record.master_variant_id
          const hashCode = this.$route.query?.i

          const res = /^(\d+)(?:-)?/ig.exec(this.$route.query.sku)
          id = res ? +res[1] || id : id

          // auto select default id by tag
          const tags = this.$route.query['q[tag_ids]'] || null
          if (tags) {
            const pts = Array.isArray(tags) ? [...tags] : [tags]
            id = +findKey(record.variants, i => i.tag_ids.some(t => pts.includes(t.toString())))
          }

          // auto select default id by imageUrl hashCode
          if (hashCode) {
            id = +findKey(record.variants, i => this.$links.hash(i.image_urls?.product) === hashCode)
          }

          if (!record.variants[id]) {
            id = +Object.keys(record.variants)[0]
          }

          this.$store.commit('history/addProduct', record)
          this.record = record
          this.selectedId = id
        })
    },

    favorite () {
      if (!this.selected) return

      this.saving = true

      const favoriteId = this.selected.favorite_products_id
      const payload = favoriteId || this.selectedId
      const service = favoriteId
        ? this.$api[this.apiRole].removeFavoriteItem
        : this.$api[this.apiRole].addItemToFavorites

      service(payload)
        .then((res) => {
          this.record.variants[this.selectedId].favorite_products_id = favoriteId ? null : res.id
        })
        .finally(() => {
          this.saving = false
        })
    },

    preOrder () {
      this.handleTrack('CLICK_PRE_ORDER')
      return this.$workflow.requestPreOrder({
        parent: this,
        product: this.record
      })
    },

    requestToPurchase () {
      this.handleTrack('CLICK_PURCHASE_REQUEST')
      return this.$workflow.requestToPurchase({
        parent: this,
        product: this.record
      })
    }
  },

  jsonld () {
    return this.$seo.jsonld(this.seo)
  }
}
</script>

<style lang="scss" src="~/assets/product.scss" scoped />
